import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LuSearch } from "react-icons/lu";
import { getAllUserForRM } from "../../redux/user/userSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  OFF_WHITE,
  PROFILE_FONT,
  filterFieldSx,
} from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const BORDER = "#E8E8E8";

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

const ONBOARDED_STATUS = 3;
const USER_CARD_MIN_HEIGHT = 228;

interface HoldingUser {
  userId: number;
  id?: number;
  firstName: string;
  lastName?: string;
  userName?: string;
  email: string;
  mobile?: string | number;
  onboardStatus: number;
  createdAt: string;
}

const getUserId = (user: HoldingUser) => user.userId ?? user.id ?? 0;

const getDisplayName = (user: HoldingUser) => {
  const fromNames = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (fromNames) return fromNames;
  if (user.userName?.trim()) return user.userName.trim();
  return "—";
};

const matchesSearchQuery = (user: HoldingUser, query: string) => {
  if (!query) return true;
  const name = getDisplayName(user).toLowerCase();
  return name !== "—" && name.includes(query.toLowerCase());
};

const matchesStatusFilter = (user: HoldingUser, statusFilter: number) => {
  if (statusFilter === -1) return true;
  if (statusFilter === ONBOARDED_STATUS) return user.onboardStatus === ONBOARDED_STATUS;
  return user.onboardStatus !== ONBOARDED_STATUS;
};

const getOnboardLabel = (status: number) =>
  status === ONBOARDED_STATUS ? "Onboarded" : "Pending";

const getOnboardColor = (status: number) =>
  status === ONBOARDED_STATUS ? "#08991F" : "#E07A2F";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const Holdings = () => {
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<number>(-1);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [users, setUsers] = useState<HoldingUser[]>([]);
  const userType = localStorage?.getItem("userType");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await dispatch(
          getAllUserForRM({
            pageIndex,
            pageSize,
            forRm: userType === "2" ? 1 : userType === "6" ? 0 : -1,
            search: debouncedSearch,
            onboardStatus: statusFilter === ONBOARDED_STATUS ? ONBOARDED_STATUS : undefined,
          })
        );

        if (response?.payload) {
          const data = response.payload.data;
          setUsers(Array.isArray(data) ? data : []);
          setTotalPages(response.payload.totalPages ?? 1);
        } else {
          setUsers([]);
          setTotalPages(1);
        }
      } catch {
        setUsers([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [dispatch, pageIndex, pageSize, userType, debouncedSearch, statusFilter]);

  const filteredUsers = useMemo(() => {
    const query = debouncedSearch.toLowerCase();
    return users.filter(
      (user) => matchesSearchQuery(user, query) && matchesStatusFilter(user, statusFilter)
    );
  }, [users, debouncedSearch, statusFilter]);

  const handleOpenClick = (user: HoldingUser) => {
    navigate(`/preferences?userId=${getUserId(user)}`);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(1);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={loading} />

      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mb: 2 }}>
          Holdings{" "}
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box component="span" sx={{ color: DARK_NAVY, fontWeight: 600 }}>
            User List
          </Box>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              User List
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
              Manage and view all registered users in the system.
            </Typography>
          </Box>

          <FormControl size="small" sx={{ ...filterFieldSx, minWidth: 130 }}>
            <InputLabel id="holdings-page-size-label">Page Size</InputLabel>
            <Select
              labelId="holdings-page-size-label"
              value={pageSize}
              label="Page Size"
              onChange={(e: SelectChangeEvent<number>) =>
                handlePageSizeChange(Number(e.target.value))
              }
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={15}>15</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <TextField
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPageIndex(1);
              }}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", pr: 1, color: DARK_NAVY_LIGHT }}>
                    <LuSearch size={18} />
                  </Box>
                ),
              }}
              sx={{
                ...filterFieldSx,
                "& .MuiOutlinedInput-root": {
                  ...filterFieldSx["& .MuiOutlinedInput-root"],
                  bgcolor: "#ffffff",
                },
              }}
            />

            <FormControl size="small" sx={{ ...filterFieldSx, minWidth: { xs: "100%", md: 180 } }}>
              <InputLabel id="holdings-status-filter">Filter</InputLabel>
              <Select
                labelId="holdings-status-filter"
                value={statusFilter}
                label="Filter"
                onChange={(e: SelectChangeEvent<number>) => {
                  setStatusFilter(Number(e.target.value));
                  setPageIndex(1);
                }}
              >
                <MenuItem value={-1}>All Status</MenuItem>
                <MenuItem value={ONBOARDED_STATUS}>Onboarded</MenuItem>
                <MenuItem value={0}>Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {!loading && users.length === 0 ? (
          <Box
            sx={{
              ...cardSx,
              py: 8,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 1,
              }}
            >
              No Users Assigned
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
              There are currently no users assigned to this Relationship Manager.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
              gap: 2.5,
            }}
          >
            {!loading && filteredUsers.length === 0 ? (
              <Box
                sx={{
                  ...cardSx,
                  gridColumn: "1 / -1",
                  py: 6,
                  px: 3,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 16,
                    fontWeight: 600,
                    color: DARK_NAVY,
                    mb: 0.5,
                  }}
                >
                  No users found
                </Typography>
                <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
                  Try adjusting your search or filter.
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((user) => {
              const displayName = getDisplayName(user);
              const initial = (displayName[0] || "U").toUpperCase();

              return (
                <Box
                  key={getUserId(user)}
                  sx={{
                    ...cardSx,
                    p: 2.5,
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    minHeight: USER_CARD_MIN_HEIGHT,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": { boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)" },
                  }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          bgcolor: ICON_BLUE,
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: PROFILE_FONT,
                          fontWeight: 700,
                          fontSize: 18,
                          flexShrink: 0,
                        }}
                      >
                        {initial}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 15,
                            fontWeight: 700,
                            color: DARK_NAVY,
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                          }}
                        >
                          {displayName}
                        </Typography>
                        <Tooltip title={user.email || ""} arrow disableHoverListener={!user.email}>
                          <Typography
                            component="span"
                            sx={{
                              display: "block",
                              fontFamily: PROFILE_FONT,
                              fontSize: 12,
                              color: DARK_NAVY_LIGHT,
                              mt: 0.5,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {user.email || "—"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 13,
                        fontWeight: 600,
                        color: getOnboardColor(user.onboardStatus),
                        mb: 1.5,
                      }}
                    >
                      {getOnboardLabel(user.onboardStatus)}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 12,
                        color: DARK_NAVY_LIGHT,
                        mb: 0.5,
                      }}
                    >
                      Onboarding Status: {user.onboardStatus}
                    </Typography>
                    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                      Created At: {formatDate(user.createdAt)}
                    </Typography>

                    <Box sx={{ mt: "auto", pt: 2, display: "flex", justifyContent: "center", width: "100%" }}>
                      <Button
                        onClick={() => handleOpenClick(user)}
                        sx={{
                          fontFamily: PROFILE_FONT,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 13,
                          bgcolor: ICON_BLUE,
                          color: "#ffffff",
                          borderRadius: "10px",
                          px: 2.5,
                          py: 1,
                          minWidth: 100,
                          boxShadow: "none",
                          "&:hover": { bgcolor: "#16304f", boxShadow: "none" },
                        }}
                      >
                        Open
                      </Button>
                    </Box>
                  </Box>
              );
            })
            )}
          </Box>
        )}

        {!loading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Pagination
              count={totalPages}
              page={pageIndex}
              onChange={(_e, page) => setPageIndex(page)}
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: PROFILE_FONT,
                  color: DARK_NAVY,
                },
                "& .Mui-selected": {
                  bgcolor: `${ICON_BLUE} !important`,
                  color: "#ffffff !important",
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Holdings;
